import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { Button } from '@fetmode/ui';

export interface PlayerMenuDockItem {
  id: string;
  label: string;
  amountCoins: number;
  disabled?: boolean;
}

export interface PlayerMenuDockRank {
  rank: number;
  label: string;
  detail: string;
  highlight?: boolean;
}

export interface PlayerMenuDockSection {
  heading: string;
  items: PlayerMenuDockItem[];
}

export interface PlayerMenuDockProps {
  titleId: string;
  title: string;
  hint: string;
  emptyLabel: string;
  closeLabel: string;
  items?: PlayerMenuDockItem[];
  sections?: PlayerMenuDockSection[];
  ranksHeading?: string;
  ranks?: PlayerMenuDockRank[];
  ranksEmptyLabel?: string;
  onSelect: (item: PlayerMenuDockItem) => void;
  onClose: () => void;
}

/** Right-docked join-gate chrome over the live player (tip menu, games, etc.). */
export function PlayerMenuDock({
  titleId,
  title,
  hint,
  emptyLabel,
  closeLabel,
  items = [],
  sections,
  ranksHeading,
  ranks = [],
  ranksEmptyLabel,
  onSelect,
  onClose,
}: PlayerMenuDockProps): ReactElement {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const resolvedSections: PlayerMenuDockSection[] =
    sections && sections.length > 0
      ? sections
      : [{ heading: title, items }];

  const hasAnyItems = resolvedSections.some((section) => section.items.length > 0);

  return (
    <div
      className="show-room__player-gate show-room__player-dock"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="show-room__player-dock-backdrop"
        aria-label={closeLabel}
        onClick={onClose}
      />
      <div className="show-room__player-gate-card show-room__player-dock-card">
        <div className="show-room__player-gate-copy">
          <div className="show-room__player-dock-heading">
            <h2 id={titleId} className="show-room__player-gate-title">
              {title}
            </h2>
            <button type="button" className="show-room__player-dock-close" onClick={onClose}>
              {closeLabel}
            </button>
          </div>
          <p className="show-room__player-gate-hint">{hint}</p>
        </div>

        <div className="show-room__player-gate-actions show-room__player-dock-scroll">
          {!hasAnyItems ? (
            <p className="show-room__player-gate-hint">{emptyLabel}</p>
          ) : (
            resolvedSections.map((section) =>
              section.items.length === 0 ? null : (
                <div key={section.heading} className="show-room__player-dock-section">
                  {sections && sections.length > 0 ? (
                    <p className="show-room__player-dock-section-title">{section.heading}</p>
                  ) : null}
                  <div
                    className="show-room__player-gate-secondary show-room__player-dock-list"
                    role="group"
                    aria-label={section.heading}
                  >
                    {section.items.map((item) => (
                      <Button
                        key={item.id}
                        label={`${item.label} (${String(item.amountCoins)} coins)`}
                        variant="ghost"
                        disabled={item.disabled}
                        onClick={() => {
                          onSelect(item);
                        }}
                      />
                    ))}
                  </div>
                </div>
              ),
            )
          )}

          {ranksHeading ? (
            <div className="show-room__player-dock-section">
              <p className="show-room__player-dock-section-title">{ranksHeading}</p>
              {ranks.length === 0 ? (
                <p className="show-room__player-gate-hint">
                  {ranksEmptyLabel ?? emptyLabel}
                </p>
              ) : (
                <ol className="show-room__player-dock-ranks" aria-label={ranksHeading}>
                  {ranks.map((entry) => (
                    <li
                      key={`${String(entry.rank)}-${entry.label}`}
                      className={
                        entry.highlight
                          ? 'show-room__player-dock-rank show-room__player-dock-rank--you'
                          : 'show-room__player-dock-rank'
                      }
                    >
                      <span className="show-room__player-dock-rank-place">#{entry.rank}</span>
                      <span className="show-room__player-dock-rank-name">{entry.label}</span>
                      <span className="show-room__player-dock-rank-score">{entry.detail}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
